// The live assistant, seen from one browser: whether it is on, whether this
// seat is heard (the call is recording), what only the host sees (the agenda,
// what is covered, the line to say next, a goal from the prep), and the
// microphone capture that runs only while all of those allow it.
//
// The Room owns every rule. This hook mirrors what the Room says and never
// decides anything: the capture starts only after the Room reports this seat
// `heard`, and stops the moment that changes. A client that is out of date and
// keeps sending audio is told so by the Room's first reply and corrects itself
// here.

import { useCallback, useEffect, useRef, useState } from "react";
import {
  ASK,
  PCM,
  VoiceGate,
  packTagged,
  resampleLinear,
  toPcm16,
  type AssistantHostState,
  type AssistantPublicState,
  type ClientMessage,
  type LateGuestView,
  type RoomMessage,
} from "@breeze/core";

/** One line of this person's private chat with Breeze. */
export interface AskMessage {
  readonly id: string;
  readonly from: "me" | "breeze";
  readonly text: string;
  /** Breeze could not answer: `text` says why. */
  readonly failed?: boolean;
}

export interface AssistantStore {
  /** This person's private chat with Breeze, oldest first. */
  readonly messages: readonly AskMessage[];
  /** A question is out and the answer has not come back. */
  readonly typing: boolean;
  /** Ask Breeze a private question. False when it was not sent. */
  readonly ask: (text: string) => boolean;
  /** The Room's last word, or null before it has said anything. */
  readonly state: AssistantPublicState | null;
  /** Host sockets only; null for everyone else, and while it is off. */
  readonly host: AssistantHostState | null;
  /** A guest in the waiting room was told the assistant is on. */
  readonly lobbyOn: boolean;
  /** A refusal worth saying, for a few seconds. */
  readonly problem: string | null;
  /** The host's switch. */
  readonly setOn: (on: boolean) => void;
  /** The host's switches for what guests get from Breeze. */
  readonly setOptions: (options: { readonly guestChat?: boolean; readonly guestGoals?: boolean; readonly guestNotes?: boolean }) => void;
  /** The host's pasted agenda. */
  readonly pasteAgenda: (text: string) => void;
  readonly sendNudge: (callId: string) => void;
  /** Host sockets only: the newest change Breeze made to a prep on its own, until dismissed. */
  /** The late-guest watcher (QUEUE row 455): host only, null when nothing to show. */
  readonly lateGuest: LateGuestView | null;
  readonly prepEdit: PrepEdit | null;
  readonly dismissPrepEdit: () => void;
  /** Handle a room message. True when it was an assistant message. */
  readonly ingest: (message: RoomMessage) => boolean;
}

/** A prep Breeze edited or made during the call, with nobody asking. */
export interface PrepEdit {
  /** Counts up, so a second edit with the same words is still news. */
  readonly seq: number;
  readonly text: string;
  readonly changed: readonly string[];
  readonly created: { readonly slug: string; readonly title: string } | null;
}

const ERROR_MS = 4000;
/** Longer than the server's own model timeout, so its answer always wins. */
const ANSWER_WAIT_MS = ASK.timeoutMs + 10_000;
const KEPT_MESSAGES = 60;

const chatKey = (roomKey: string): string => `breeze.ask.${roomKey}`;

function loadChat(roomKey: string | undefined): AskMessage[] {
  if (!roomKey) return [];
  try {
    const raw = window.sessionStorage.getItem(chatKey(roomKey));
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    const kept: AskMessage[] = [];
    for (const m of parsed) {
      if (typeof m !== "object" || m === null) continue;
      const { id, from, text, failed } = m as Record<string, unknown>;
      if (typeof id !== "string" || typeof text !== "string") continue;
      if (from !== "me" && from !== "breeze") continue;
      kept.push({ id, from, text, ...(failed === true ? { failed: true } : {}) });
    }
    return kept.slice(-KEPT_MESSAGES);
  } catch {
    return [];
  }
}

function saveChat(roomKey: string | undefined, messages: readonly AskMessage[]): void {
  if (!roomKey) return;
  try {
    window.sessionStorage.setItem(chatKey(roomKey), JSON.stringify(messages.slice(-KEPT_MESSAGES)));
  } catch {
    // Private mode or a full store: the chat stays in memory.
  }
}

function newAskId(): string {
  try {
    return crypto.randomUUID();
  } catch {
    return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
  }
}

// An AudioWorklet that hands the main thread each block of mono samples.
const WORKLET = `
class BreezePcm extends AudioWorkletProcessor {
  process(inputs) {
    const ch = inputs[0] && inputs[0][0];
    if (ch) this.port.postMessage(ch.slice(0));
    return true;
  }
}
registerProcessor("breeze-pcm", BreezePcm);
`;

interface Capture {
  stop: () => void;
}

/** Reads the microphone track into 16 kHz PCM frames and hands each loud one
 * to `emit`. Returns null when the browser cannot. */
async function startCapture(track: MediaStreamTrack, emit: (frame: ArrayBuffer) => void): Promise<Capture | null> {
  const Ctx: typeof AudioContext | undefined =
    window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctx) return null;
  let ctx: AudioContext;
  try {
    ctx = new Ctx({ sampleRate: PCM.sampleRate });
  } catch {
    ctx = new Ctx();
  }
  const rate = ctx.sampleRate;
  const gate = new VoiceGate();
  let pending: Float32Array[] = [];
  let pendingLen = 0;
  const frameAtRate = Math.round((PCM.frameSamples * rate) / PCM.sampleRate);

  const take = (block: Float32Array): void => {
    pending.push(block);
    pendingLen += block.length;
    if (pendingLen < frameAtRate) return;
    const joined = new Float32Array(pendingLen);
    let at = 0;
    for (const part of pending) {
      joined.set(part, at);
      at += part.length;
    }
    pending = [];
    pendingLen = 0;
    // A muted microphone stays silent without being disabled in the graph, so
    // the gate alone would pass nothing; check the switch too.
    if (!track.enabled || track.readyState !== "live") return;
    const frame = resampleLinear(joined, rate, PCM.sampleRate);
    if (!gate.pass(frame)) return;
    const pcm = toPcm16(frame);
    emit(pcm.buffer.slice(pcm.byteOffset, pcm.byteOffset + pcm.byteLength) as ArrayBuffer);
  };

  const source = ctx.createMediaStreamSource(new MediaStream([track]));
  let node: AudioNode;
  let url: string | null = null;
  try {
    url = URL.createObjectURL(new Blob([WORKLET], { type: "application/javascript" }));
    await ctx.audioWorklet.addModule(url);
    const worklet = new AudioWorkletNode(ctx, "breeze-pcm", { numberOfOutputs: 0 });
    worklet.port.onmessage = (e: MessageEvent<Float32Array>) => take(e.data);
    source.connect(worklet);
    node = worklet;
  } catch {
    // No worklet (an older Safari): the deprecated node still works.
    const proc = ctx.createScriptProcessor(4096, 1, 1);
    proc.onaudioprocess = (e) => take(new Float32Array(e.inputBuffer.getChannelData(0)));
    const mute = ctx.createGain();
    mute.gain.value = 0;
    source.connect(proc);
    proc.connect(mute);
    mute.connect(ctx.destination);
    node = proc;
  } finally {
    if (url) URL.revokeObjectURL(url);
  }
  if (ctx.state === "suspended") void ctx.resume().catch(() => undefined);

  return {
    stop: () => {
      try {
        source.disconnect();
        node.disconnect();
      } catch {
        // Already disconnected.
      }
      void ctx.close().catch(() => undefined);
    },
  };
}

/** Another person's microphone as this page receives it. */
export interface RemoteAudio {
  readonly id: string;
  readonly track: MediaStreamTrack;
}

const NO_REMOTE: readonly RemoteAudio[] = [];

export function useAssistant(
  send: (message: ClientMessage) => void,
  sendAudio: (frame: ArrayBuffer) => void,
  /** The microphone track in use, or null (a phone with no mic, a muted device). */
  micTrack: MediaStreamTrack | null,
  /** Told whether the assistant is on, so the socket can ping faster. */
  onOnChange: (on: boolean) => void,
  /** Names this seat's chat in sessionStorage. Without it the chat is memory only. */
  roomKey?: string,
  /** The other people's audio in the call. Used only while the Room says a quiet
   * recording is running (state.capture), when this page hears them for the
   * transcript because no guest's page has been told to. */
  remote: readonly RemoteAudio[] = NO_REMOTE,
): AssistantStore {
  const [messages, setMessages] = useState<AskMessage[]>(() => loadChat(roomKey));
  const [pending, setPending] = useState<string | null>(null);
  const pendingRef = useRef<string | null>(null);
  const answerTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [state, setState] = useState<AssistantPublicState | null>(null);
  const [host, setHost] = useState<AssistantHostState | null>(null);
  const [lobbyOn, setLobbyOn] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [prepEdit, setPrepEdit] = useState<PrepEdit | null>(null);
  const [lateGuest, setLateGuest] = useState<LateGuestView | null>(null);
  const prepSeq = useRef(0);
  const dismissPrepEdit = useCallback(() => setPrepEdit(null), []);
  const problemTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sendRef = useRef(send);
  sendRef.current = send;
  const audioRef = useRef(sendAudio);
  audioRef.current = sendAudio;
  const onOnChangeRef = useRef(onOnChange);
  onOnChangeRef.current = onOnChange;

  const say = useCallback((text: string) => {
    setProblem(text);
    if (problemTimer.current) clearTimeout(problemTimer.current);
    problemTimer.current = setTimeout(() => setProblem(null), ERROR_MS);
  }, []);

  useEffect(
    () => () => {
      if (problemTimer.current) clearTimeout(problemTimer.current);
      if (answerTimer.current) clearTimeout(answerTimer.current);
    },
    [],
  );

  useEffect(() => {
    saveChat(roomKey, messages);
  }, [roomKey, messages]);

  /** The answer (or the refusal) for the open question has landed. */
  const release = useCallback(() => {
    if (answerTimer.current) clearTimeout(answerTimer.current);
    answerTimer.current = null;
    pendingRef.current = null;
    setPending(null);
  }, []);

  /** Breeze's side of one question, once however many times it is told. */
  const reply = useCallback((id: string, text: string, failed: boolean) => {
    setMessages((prev) => {
      if (!prev.some((m) => m.id === id && m.from === "me")) return prev;
      if (prev.some((m) => m.id === `${id}:a`)) return prev;
      const line: AskMessage = { id: `${id}:a`, from: "breeze", text, ...(failed ? { failed: true } : {}) };
      return [...prev, line].slice(-KEPT_MESSAGES);
    });
  }, []);

  const on = state?.on === true;
  useEffect(() => {
    onOnChangeRef.current(on);
  }, [on]);

  // Capture runs only while the Room says this seat is heard (on, and the
  // call recording) and it is not paused. Anything else stops it at once.
  const listening = on && state?.heard === true && state.paused === null && state.mode === "listening";
  useEffect(() => {
    if (!listening || !micTrack) return;
    let cancelled = false;
    let capture: Capture | null = null;
    void startCapture(micTrack, (frame) => audioRef.current(frame)).then((c) => {
      if (cancelled) c?.stop();
      else capture = c;
    });
    return () => {
      cancelled = true;
      capture?.stop();
    };
  }, [listening, micTrack]);

  // The host's page hears everyone else while the recording is quiet, one
  // capture per person, each sent tagged with whose it is. It stops the moment
  // the Room stops saying so (the recording was revealed or stopped).
  const capturing = on && state?.capture === true && state.paused === null;
  const remoteKey = remote.map((r) => r.id + ":" + r.track.id).join("|");
  const remoteRef = useRef(remote);
  remoteRef.current = remote;
  useEffect(() => {
    if (!capturing) return;
    let cancelled = false;
    const captures: Capture[] = [];
    for (const r of remoteRef.current) {
      void startCapture(r.track, (frame) => {
        const tagged = packTagged(r.id, frame);
        if (tagged) audioRef.current(tagged);
      }).then((c) => {
        if (!c) return;
        if (cancelled) c.stop();
        else captures.push(c);
      });
    }
    return () => {
      cancelled = true;
      for (const c of captures) c.stop();
    };
  }, [capturing, remoteKey]);

  const ingest = useCallback(
    (message: RoomMessage): boolean => {
      switch (message.type) {
        case "assistantState":
          setState(message.state);
          if (!message.state.on) setHost(null);
          return true;
        case "assistantHost":
          setHost(message.host);
          return true;
        case "lateGuest":
          setLateGuest(message.late);
          return true;
        case "prepEdited":
          prepSeq.current += 1;
          setPrepEdit({ seq: prepSeq.current, text: message.text, changed: message.changed, created: message.created });
          return true;
        case "waiting":
          setLobbyOn(message.assistantOn === true);
          return false;
        case "welcome":
          setLobbyOn(false);
          return false;
        case "assistantAnswer":
          reply(message.id, message.text ?? message.error ?? "Breeze could not answer just now. Try again.", message.text === undefined);
          if (pendingRef.current === message.id) release();
          return true;
        case "error":
          if (message.code === "assistant_denied" || message.code === "assistant_agenda") {
            say(message.message);
            return true;
          }
          if (message.code === "assistant_ask") {
            const open = pendingRef.current;
            if (open) {
              reply(open, message.message, true);
              release();
            }
            return true;
          }
          return false;
        default:
          return false;
      }
    },
    [say, reply, release],
  );

  const ask = useCallback(
    (text: string): boolean => {
      const t = text.trim();
      if (!t || t.length > ASK.textChars || pendingRef.current) return false;
      const id = newAskId();
      pendingRef.current = id;
      setPending(id);
      setMessages((prev) => [...prev, { id, from: "me" as const, text: t }].slice(-KEPT_MESSAGES));
      sendRef.current({ type: "assistantAsk", id, text: t });
      answerTimer.current = setTimeout(() => {
        if (pendingRef.current !== id) return;
        reply(id, "Breeze did not answer. Try again.", true);
        release();
      }, ANSWER_WAIT_MS);
      return true;
    },
    [reply, release],
  );

  const setOn = useCallback((next: boolean) => sendRef.current({ type: "assistantSet", on: next }), []);
  const setOptions = useCallback(
    (options: { readonly guestChat?: boolean; readonly guestGoals?: boolean; readonly guestNotes?: boolean }) =>
      sendRef.current({ type: "assistantOptions", ...options }),
    [],
  );
  const pasteAgenda = useCallback((text: string) => sendRef.current({ type: "assistantAgenda", text }), []);

  const sendNudge = useCallback((callId: string) => sendRef.current({ type: "lateNudgeSend", callId }), []);

  return { messages, typing: pending !== null, ask, state, host, lobbyOn, problem, setOn, setOptions, pasteAgenda, sendNudge, lateGuest, prepEdit, dismissPrepEdit, ingest };
}
